import React from "react";
import { X, AlertTriangle, UserPlus } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import { ISalesRepInfo } from "../../models/epcCustomer.model";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

interface ConfirmCustomerAndQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  customerData: {
    customerName: string;
    mobileNumber: string;
    salesRepresentative?: string;
    cseID?: string;
  };
  isLoading?: boolean;
  salesRepList?: ISalesRepInfo[];
}

const ConfirmCustomerAndQuoteModal: React.FC<
  ConfirmCustomerAndQuoteModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  customerData,
  isLoading = false,
  salesRepList,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  if (!isOpen) return null;
  const getDisplayName = () => {
    if (customerData.cseID === currentUser?.id) {
      return `self - ${currentUser?.username}`;
    }

    if (customerData.salesRepresentative) {
      const foundRep = salesRepList?.find(
        (rep) => rep._id === customerData.salesRepresentative,
      );
      if (foundRep) return foundRep.name;
    }

    if (customerData.cseID) {
      const foundCse = salesRepList?.find(
        (rep) => rep._id === customerData.cseID,
      );
      if (foundCse) return foundCse.name;
    }

    return "Not assigned";
  };

  const getRoleLabel = () => {
    if (customerData.cseID) return "CSE:";
    if (customerData.salesRepresentative) return "Sales Representative:";
    return "Assigned To:";
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10  rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Customer Not Found
              </h2>
              <p className="text-gray-600 text-sm">
                Would you like to create this customer?
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-600/80 hover:text-gray-800 transition-colors p-1 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <UserPlus className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Customer Details:</span>
            </div>
            <div className="ml-7 space-y-2">
              <p className="text-sm">
                <span className="font-medium text-gray-700">Name:</span>{" "}
                {customerData.customerName}
              </p>
              <p className="text-sm">
                <span className="font-medium text-gray-700">Mobile:</span>{" "}
                {customerData.mobileNumber}
              </p>
              <p className="text-sm">
                <span className="font-medium text-gray-700">
                  {getRoleLabel()}
                </span>{" "}
                {getDisplayName()}
              </p>
            </div>
          </div>

          <p className="text-sm text-amber-600 mb-6">
            No customer found with this mobile number. Would you like to create
            a new customer and generate the quotation for them?
          </p>

          <div className="flex gap-3 justify-end">
            <Button
              type={ButtonType.Cancel}
              onClick={onClose}
              disabled={isLoading}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              onClick={onConfirm}
              disabled={isLoading}
              className="text-xs inline-flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Create Customer & Quotation
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCustomerAndQuoteModal;
